/**
 * CustomerDataProvider — v1.4.6 (Phase 2 fix #1)
 *
 * Single source of truth for `/api/customer/dashboard-data`. Before this
 * file existed, 16 different components each called `useCustomerData()`
 * independently, each firing its own ~2 s dashboard fetch on mount. On
 * a typical page load that meant ~3 concurrent identical fetches
 * (App.js + TopBar.jsx + the page itself), turning a 2-second backend
 * into a 6-second wall-clock — and 10–15 s on production where pod ↔
 * Supabase RTT is higher.
 *
 * This provider consolidates all 16 consumers behind a single fetch
 * that runs once per refresh cycle and broadcasts the result through
 * React Context.
 *
 * GRACEFUL DEGRADATION
 * --------------------
 * The risk of a shared fetch is that a single failure now affects
 * every consumer. We mitigate by:
 *   1. PRESERVING the previous `customerData` on transient errors —
 *      `customerData` only gets nulled when there's never been a
 *      successful fetch in this session. The dashboard keeps rendering
 *      the last good numbers; the error surfaces via `error` and the
 *      caller can show a non-blocking retry banner.
 *   2. EXPOSING `reload()` so any consumer can manually retry without
 *      having to know about the provider implementation.
 *   3. NEVER throwing from the context value — failures live in
 *      `error` (string) and `isLoading` (false on failure).
 *
 * The default export of `useCustomerData.js` is now a thin wrapper
 * around `useContext(CustomerDataContext)`, so all 16 consumers work
 * unchanged.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getCurrentCustomer, AUTH_EVENT, clearCustomer, checkSessionDrift } from "../lib/customerSession";
import { BACKEND_URL } from "../lib/backendUrl";

const BACKEND = BACKEND_URL;
const REFRESH_MS = 5 * 60 * 1000; // 5 minutes — unchanged from per-hook polling

// --- Response shaper ------------------------------------------------------
// Identical to the pre-provider version. Kept here so callers don't have
// to know it exists.
function shapeCustomerData(json) {
  if (!json) return null;
  const liq    = json.liquidity     || {};
  const prof   = json.profitability || {};
  const eff    = json.efficiency    || {};
  const gst    = json.gst           || {};
  const debt   = json.debtors       || {};
  const inv    = json.inventory     || {};
  const fcst   = json.forecasting   || {};
  const health = json.health        || {};
  const raw    = json.raw           || {};

  return {
    ...json,
    financial: {
      cash:             raw.cash ?? liq.cash ?? null,
      revenue_mtd:      raw.revenue_mtd ?? prof.revenue_mtd ?? null,
      expenses_mtd:     raw.expenses_mtd ?? prof.expenses_mtd ?? null,
      revenue_growth:   prof.revenue_growth_pct ?? null,
      ebitda:           prof.ebitda ?? null,
      net_profit:       prof.net_profit ?? null,
      gross_margin_pct: prof.gross_margin_pct ?? null,
    },
    derived: {
      ccc_days:         eff.ccc_days ?? null,
      dso_days:         eff.dso_days ?? null,
      dio_days:         eff.dio_days ?? null,
      dpo_days:         eff.dpo_days ?? null,
      working_capital:  liq.working_capital ?? null,
      total_debtors:    debt.total_debtors ?? 0,
      total_creditors:  liq.total_creditors ?? 0,
    },
    // v1.4.5 — flat array with properties attached so both shapes resolve.
    debtors: (() => {
      const arr = debt.debtors_raw || [];
      arr.debtors_raw      = debt.debtors_raw      || [];
      arr.critical_debtors = debt.critical_debtors || [];
      arr.debtor_count     = debt.debtor_count ?? null;
      arr.total_debtors    = debt.total_debtors ?? 0;
      return arr;
    })(),
    gst:        gst,
    inventory:  inv,
    forecasting: fcst,
    health:     health,
  };
}


// --- Context shape --------------------------------------------------------
const DEFAULT_VALUE = {
  customerData:    null,
  dataAvailable:   false,
  isLoading:       true,
  error:           null,
  isActiveCustomer:false,
  customer:        null,
  companyName:     null,
  reload:          () => {},
};

export const CustomerDataContext = createContext(DEFAULT_VALUE);


export function CustomerDataProvider({ children }) {
  const [customerData,  setCustomerData]  = useState(null);
  const [dataAvailable, setDataAvailable] = useState(false);
  const [isLoading,     setIsLoading]     = useState(true);
  const [error,         setError]         = useState(null);

  // Keep customer in state so a same-tab login/logout re-reads the session
  // and re-fires the dashboard fetch immediately. Previously this was read
  // once per render from sessionStorage; the Provider sits ABOVE App and does
  // not re-render on route changes, so a fresh login was never picked up until
  // an unrelated re-render — part of the "login needs 2-3 attempts" bug.
  const [customer, setCustomer] = useState(() => {
    try { return getCurrentCustomer(); } catch (_e) { return null; }
  });
  const customerRef = useRef(customer);
  customerRef.current = customer;

  useEffect(() => {
    const refresh = () => {
      try { setCustomer(getCurrentCustomer()); } catch (_e) { setCustomer(null); }
    };
    window.addEventListener(AUTH_EVENT, refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(AUTH_EVENT, refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const inFlightRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    const c = customerRef.current;
    const cid = c?.id || c?.customer_id;
    if (!cid) {
      setCustomerData(null);
      setDataAvailable(false);
      setIsLoading(false);
      return;
    }
    // Coalesce overlapping calls. If `reload()` is hit while the prior
    // fetch is still in flight (e.g. consumer mounts during the
    // pipeline-done event), share the same promise instead of firing
    // a second identical request.
    if (inFlightRef.current) {
      return inFlightRef.current;
    }
    setError(null);
    const promise = (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/customer/dashboard-data`, { credentials: "include" });
        if (!r.ok) {
          // 2026-11 — identity now comes from the session cookie; a 401
          // means the session expired/was revoked. Treat it as a logout
          // (App.js's route guard then sends the user to /login).
          if (r.status === 401) { clearCustomer(); }
          throw new Error(`HTTP ${r.status}`);
        }
        const json = await r.json();
        checkSessionDrift(json?.customer_id);
        setCustomerData(shapeCustomerData(json));
        setDataAvailable(!!json?.data_available);
      } catch (e) {
        // GRACEFUL DEGRADATION — preserve the last good `customerData`.
        // Surface the error but don't nuke the rendered state of all
        // 16 consumers. They keep showing last-known-good numbers and
        // the (optional) error banner.
        setError(e.message || "Could not load dashboard data");
        // dataAvailable stays at its previous value — if we had data
        // before, the cards keep rendering it.
      } finally {
        setIsLoading(false);
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchOnce();
    const id = setInterval(fetchOnce, REFRESH_MS);
    // Pipeline-completion event — fired by ExcelUploadDrawer.commit()
    // and any future provider that runs `/api/integrations/sync`. Lets
    // the dashboard refresh instantly instead of waiting for the next
    // poll tick.
    const onPipelineDone = () => { fetchOnce(); };
    window.addEventListener("core:pipeline-done", onPipelineDone);
    return () => {
      clearInterval(id);
      window.removeEventListener("core:pipeline-done", onPipelineDone);
    };
  }, [customer?.id, customer?.customer_id]);

  const isActiveCustomer = !!(customer && customer.status === "active");
  const companyName = customerData?.company_name || customerData?.meta?.tally_company || null;

  const value = {
    customerData,
    dataAvailable,
    isLoading,
    error,
    isActiveCustomer,
    customer,
    companyName,
    reload: fetchOnce,
  };

  return (
    <CustomerDataContext.Provider value={value}>
      {children}
    </CustomerDataContext.Provider>
  );
}


// Thin hook for consumers — preserves the original API surface so the
// 16 existing call sites work unchanged.
export function useCustomerDataContext() {
  return useContext(CustomerDataContext);
}
